import { z } from 'zod';
import { v4 } from 'uuid';

export const units = [
  'Milligram',
  'Gram',
  'Kilogram',
  'Millilitre',
  'Deciliter',
  'Liter',
  'Teaspoon(s)',
  'Tablespoon(s)',
  'Cup(s)',
  'Piece(s)',
  'Pinch',
  'Clove(s)',
  'Can(s)',
  'Bunch',
] as const;

export type Unit = (typeof units)[number] | '';

// Units amounts can be shown in; ounces and pounds only after conversion
export type DisplayUnit = Unit | 'Ounce(s)' | 'Pound(s)';

// Recipes saved before servings existed are read as serving this many
export const DEFAULT_SERVINGS = 4;

const StepSchema = z
  .object({
    text: z.string().min(1),
  })
  .required();

const IngredientSchema = z
  .object({
    text: z.string().min(1).max(256),
    amount: z.number().positive(),
    unit: z.enum([...units, '']).default(''),
  })
  .required();

// Per serving; every value is optional
export const NutritionSchema = z.object({
  kcal: z.number().nonnegative().optional(),
  protein: z.number().nonnegative().optional(),
  carbs: z.number().nonnegative().optional(),
  fat: z.number().nonnegative().optional(),
});

export const RecipeSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => v4()),
  title: z.string().min(1).max(1024).default(''),
  subTitle: z.string().max(1024).default(''),
  preparationTime: z.number().positive().default(0),
  servings: z.number().int().positive().max(99).default(DEFAULT_SERVINGS),
  rating: z.record(z.number()).default({}),
  steps: z.array(StepSchema).max(512).default([]),
  ingredients: z.array(IngredientSchema).default([]),
  // The cover; also images[0] once a recipe has more than one picture
  image: z.string().default(''),
  // Optional: recipes saved before these fields have none
  images: z.array(z.string()).max(20).optional(),
  notes: z.string().max(5000).optional(),
  sourceUrl: z.string().url().max(2048).or(z.literal('')).optional(),
  nutrition: NutritionSchema.optional(),
  shared: z.boolean().default(false),
  sharedWith: z.array(z.string()).default([]),
  tags: z.array(z.string().trim().min(1).max(50)).max(20).default([]),
  // Set when the recipe is first saved; older recipes have none
  createdAt: z.number().optional(),
  user: z.string().optional(),
});

export const RecipeFormSchema = z.object({
  recipe: RecipeSchema,
  // A new cover picture
  images: z.array(z.custom<File>()).default([]),
  // More pictures to upload
  photos: z.array(z.custom<File>()).optional(),
});

export type Recipe = z.infer<typeof RecipeSchema>;
export type Step = z.infer<typeof StepSchema>;
export type Ingredient = z.infer<typeof IngredientSchema>;
export type Nutrition = z.infer<typeof NutritionSchema>;
export type RecipePayload = z.infer<typeof RecipeFormSchema>;
